import React, { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Typography,
  Avatar,
  Button,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  FormControl,
  Select,
  MenuItem,
  Stack,
  Card,
  CardContent,
  CardActions,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tooltip
} from '@mui/material';
import { BsArrowLeft, BsEyeFill, BsPencil, BsTrash } from 'react-icons/bs';
import { useNavigate } from 'react-router';
import toast from 'react-hot-toast';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import MainCard from 'components/MainCard';
import { getCoach } from 'services/coach/getCoach';
import { getCourse } from 'services/course/getCourse';
import { getAsana } from 'services/asana/getAsana';
import { TagOutlined } from '@ant-design/icons';
import { FaChartBar, FaDumbbell } from 'react-icons/fa';
import { updateApproveorRejectStatus } from 'services/content-oversight/updateApproveorRejectStatus';
import { deleteAsana } from 'services/content-oversight/deleteAsana';

const getCoachStatusProps = (status) => {
  switch (status) {
    case 1:
      return { label: 'Active', color: 'success' };
    case 2:
      return { label: 'Inactive', color: 'error' };
    default:
      return { label: 'Unknown', color: 'default' };
  }
};

const getstatusProps = (asanaStatus) => {
  switch (asanaStatus) {
    case 1:
      return { label: 'Approved', color: 'success' };
    case 0:
      return { label: 'Pending', color: 'warning' };
    case 2:
      return { label: 'Rejected', color: 'error' };
    default:
      return { label: 'Unknown', color: 'default' };
  }
};

export default function CourseOversightDashboard() {
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState('coaches');
  const [selectedCoach, setSelectedCoach] = useState(null);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [openRejectDialog, setOpenRejectDialog] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [selectedAsanaId, setSelectedAsanaId] = useState(null);

  const queryClient = useQueryClient();

  const {
    data: coachesData,
    isLoading: coachesLoading,
    error: coachesError
  } = useQuery({
    queryKey: ['coaches'],
    queryFn: () => getCoach()
  });

  const {
    data: coursesDatas,
    isLoading: coursesLoading,
    error: coursesError
  } = useQuery({
    queryKey: ['courses', selectedCoach?.coachid],
    queryFn: () => getCourse({ coachid: selectedCoach?.coachid }),
    enabled: !!selectedCoach
  });

  const {
    data: asanasData,
    isLoading: asanasLoading,
    error: asanasError,
    refetch: refetchAsanas
  } = useQuery({
    queryKey: ['asanas', selectedCourse?.courseId],
    queryFn: () => getAsana({ courseId: selectedCourse?.courseId }),
    enabled: !!selectedCourse
  });

  const coursesData = coursesDatas?.response || [];
  const asanas = asanasData?.response || [];

  const handleCoachSelect = (coach) => {
    setSelectedCoach(coach);
    setViewMode('coachCourses');
  };

  const handleBackToCoaches = () => {
    setViewMode('coaches');
    setSelectedCoach(null);
    setSelectedCourse(null);
  };

  const handleViewAsanas = (course) => {
    setSelectedCourse(course);
    setViewMode('courseAsanas');
  };

  const handleBackToCourses = () => {
    setViewMode('coachCourses');
    setSelectedCourse(null);
  };

  const updateApproveorRejectStatusMutation = useMutation({
    mutationFn: updateApproveorRejectStatus,
    onSuccess: (data) => {
      toast.success(data);
      queryClient.invalidateQueries(['asanas']);
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to update status');
    }
  });

  const handleApproveAsana = (asanaId) => {
    updateApproveorRejectStatusMutation.mutate({ asanaId: asanaId, statusId: 1 });
  };

  const handleRejectAsana = (asanaId, reason) => {
    console.log('Sending to backend:', { asanaId, statusId: 2, rejectionReason: reason });
    updateApproveorRejectStatusMutation.mutate({
      asanaId,
      statusId: 2,
      rejectionReason: reason
    });
  };

  const handleViewAsana = (asana) => {
    if (asana?.image) {
      window.open(asana.image, '_blank');
    }
  };

  const deleteAsanaMutation = useMutation({
    mutationFn: deleteAsana,

    onSuccess: () => {
      toast.success('Asana deleted successfully');
      queryClient.invalidateQueries(['asanas']);
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to delete asana');
    }
  });

  const handleDeleteAsana = (asanaId) => {
    const confirmed = window.confirm('Are you sure you want to delete this asana? This action cannot be undone.');
    if (confirmed) {
      deleteAsanaMutation.mutate({ asanaId });
    }
  };

  const handleOpenRejectDialog = (asanaId) => {
    setSelectedAsanaId(asanaId);
    setRejectReason('');
    setOpenRejectDialog(true);
  };

  const handleCloseRejectDialog = () => {
    setOpenRejectDialog(false);
    setSelectedAsanaId(null);
    setRejectReason('');
  };

  const handleConfirmReject = () => {
    if (!rejectReason.trim()) {
      toast.error('Please provide a reason for rejection');
      return;
    }

    handleRejectAsana(selectedAsanaId, rejectReason);
    handleCloseRejectDialog();
  };

  if (coachesError) {
    toast.error('Failed to load coaches');
    return <Typography color="error">Error: {coachesError.message}</Typography>;
  }

  if (coursesError && viewMode !== 'coaches') {
    toast.error('Failed to load courses');
    return <Typography color="error">Error: {coursesError.message}</Typography>;
  }

  if (asanasError && viewMode === 'courseAsanas') {
    toast.error('Failed to load asanas');
    return <Typography color="error">Error: {asanasError.message}</Typography>;
  }

  if (viewMode === 'coaches') {
    return (
      <MainCard title="Coaches List">
        {coachesLoading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Coach</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Joined</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {coachesData?.length ? (
                  coachesData.map((coach) => {
                    const { label, color } = getCoachStatusProps(coach.status);
                    return (
                      <TableRow key={coach.coachid}>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Avatar sx={{ width: 56, height: 56 }}>
                              <img
                                src={coach.avatar || 'https://avataaars.io/?avatarStyle=Circle'}
                                alt="Avatar"
                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                              />
                            </Avatar>
                            <Typography>{coach.name}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell>{coach.email}</TableCell>
                        <TableCell>{new Date(coach.created).toLocaleDateString()}</TableCell>
                        <TableCell>
                          <Chip label={label} color={color} />
                        </TableCell>
                        <TableCell align="right">
                          <Button variant="outlined" onClick={() => handleCoachSelect(coach)}>
                            View Courses
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      <Typography>No coaches found</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </MainCard>
    );
  }

  if (viewMode === 'coachCourses' && selectedCoach) {
    const { label, color } = getCoachStatusProps(selectedCoach.asanaStatus);

    return (
      <Box>
        {/* Header and Back Button */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h5" fontWeight={700}>
            Coach: {selectedCoach.name}
          </Typography>
          <Button variant="outlined" startIcon={<BsArrowLeft />} onClick={handleBackToCoaches}>
            Back to Coaches
          </Button>
        </Box>

        {/* Coach Profile */}
        <Paper
          sx={{
            p: 3,
            display: 'flex',
            alignItems: 'center',
            gap: 3,
            mb: 5
          }}
        >
          <Avatar sx={{ width: 80, height: 80 }}>
            <img
              src={selectedCoach.avatar || 'https://avataaars.io/?avatarStyle=Circle'}
              alt="Avatar"
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          </Avatar>
          <Box>
            <Typography variant="h6" fontWeight={600}>
              {selectedCoach.name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {selectedCoach.email}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Joined on: {new Date(selectedCoach.created).toLocaleDateString()}
            </Typography>
            <Chip label={label} color={color} size="small" sx={{ mt: 1 }} />
          </Box>
        </Paper>

        {/* Courses Grid */}
        {coursesLoading ? (
          <Box display="flex" justifyContent="center" py={5}>
            <CircularProgress />
          </Box>
        ) : coursesData?.length ? (
          <Grid container spacing={3}>
            {coursesData.map((course) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={course.courseId}>
                <Card
                  sx={{
                    height: '100%'
                  }}
                >
                  <CardContent>
                    <Typography variant="h6" fontWeight={600} color="primary.main" gutterBottom sx={{ textTransform: 'capitalize' }}>
                      {course.courseName}
                    </Typography>

                    <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                      <TagOutlined />
                      <Typography variant="body2">Category: {course.category}</Typography>
                    </Box>
                    <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                      <FaChartBar />
                      <Typography variant="body2">Difficulty: {course.difficulty}</Typography>
                    </Box>
                    <Box display="flex" alignItems="center" gap={1}>
                      <FaDumbbell />
                      <Typography variant="body2">Asanas: {course.asanaCount}</Typography>
                    </Box>
                  </CardContent>
                  <CardActions sx={{ p: 2 }}>
                    <Button variant="contained" fullWidth size="medium" onClick={() => handleViewAsanas(course)}>
                      View Asanas
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        ) : (
          <Typography align="center" mt={4}>
            No courses found for this coach.
          </Typography>
        )}
      </Box>
    );
  }

  if (viewMode === 'courseAsanas' && selectedCourse) {
    return (
      <>
        <MainCard
          title={`Asanas for ${selectedCourse.courseName}`}
          secondary={
            <Button variant="outlined" startIcon={<BsArrowLeft />} onClick={handleBackToCourses}>
              Back to Courses
            </Button>
          }
        >
          {asanasLoading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : asanas?.length ? (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Asana Name</TableCell>
                    <TableCell>Asana</TableCell>
                    <TableCell>Created Date</TableCell>
                    <TableCell>Created Time</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {asanas.map((asana) => {
                    const { label, color } = getstatusProps(asana.asanaStatus);
                    return (
                      <TableRow key={asana.id}>
                        <TableCell>{asana?.posturename || 'Unknown'}</TableCell>
                        <TableCell>
                          <Avatar src={asana.image} alt={asana.posturename} sx={{ width: 56, height: 56 }} variant="square" />
                        </TableCell>
                        <TableCell>{new Date(asana.created_at).toLocaleDateString()}</TableCell>
                        <TableCell>{new Date(asana.created_at).toLocaleTimeString()}</TableCell>
                        <TableCell>
                          <Chip label={label} color={color} size="small" />
                        </TableCell>
                        <TableCell align="right">
                          <Stack direction="row" spacing={1.5} justifyContent="flex-end" alignItems="center">
                            <FormControl
                              size="small"
                              sx={{
                                minWidth: 140,
                                opacity: asana.asanaStatus === 1 ? 1 : 1,
                                pointerEvents: asana.asanaStatus === 1 ? 'none' : 'auto',
                                cursor: asana.asanaStatus === 1 ? 'not-allowed' : 'pointer',
                                '& .MuiOutlinedInput-root': {
                                  backgroundColor: '#f5f5f5'
                                },
                                '& .MuiSelect-select': {
                                  paddingY: '8px'
                                }
                              }}
                            >
                              <Select
                                displayEmpty
                                value=""
                                disabled={asana.asanaStatus === 1}
                                onChange={(e) => {
                                  const [actionCode, id] = e.target.value.split(':');
                                  const asanaId = Number(id);
                                  if (actionCode === '1') {
                                    handleApproveAsana(asanaId);
                                  } else if (actionCode === '2') {
                                    handleOpenRejectDialog(asanaId);
                                  }
                                }}
                                renderValue={() => 'Change Status'}
                              >
                                <MenuItem value={`1:${asana.id}`}>✅ Approve</MenuItem>
                                <MenuItem value={`2:${asana.id}`}>❌ Reject</MenuItem>
                              </Select>
                            </FormControl>

                            <Tooltip title="View Asana">
                              <IconButton color="primary" onClick={() => handleViewAsana(asana)} sx={{ borderRadius: '12px' }}>
                                <BsEyeFill />
                              </IconButton>
                            </Tooltip>

                            <Tooltip title="Delete Asana">
                              <IconButton color="error" onClick={() => handleDeleteAsana(asana.id)} sx={{ borderRadius: '12px' }}>
                                <BsTrash />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography>No asanas found for this course</Typography>
          )}
        </MainCard>

        {/* Reject Reason Dialog */}
        <Dialog open={openRejectDialog} onClose={handleCloseRejectDialog}>
          <DialogTitle>Reject Asana</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="Reason for Rejection"
              type="text"
              fullWidth
              multiline
              rows={4}
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              helperText="Please provide a reason for rejecting this asana."
            />
          </DialogContent>
          <DialogActions>
            <Button variant="outlined" onClick={handleCloseRejectDialog}>
              Cancel
            </Button>
            <Button onClick={handleConfirmReject} variant="contained" color="error">
              Confirm Reject
            </Button>
          </DialogActions>
        </Dialog>
      </>
    );
  }

  return null;
}
